/* assets/quick-view.js
   External module. Loaded once from theme.liquid.

   Two responsibilities:
     1. Delegated open/prefetch off [data-qv-trigger] on product cards.
        No custom element wraps the trigger, so nothing here touches
        Horizon's ref/on: binding system.
     2. <quick-view-gallery>, defined here so injected section markup
        upgrades on insertion. Section HTML never carries a script node -
        DOMParser/innerHTML script nodes are permanently non-executable,
        which is the bug this rebuild exists to correct. */

const MODAL_ID = 'QuickViewModal';
const PREFETCH_DELAY = 150;

const cache = new Map();
let controller = null;
let prefetchTimer = null;
let lastTrigger = null;

const getModal = () => document.getElementById(MODAL_ID);

async function fetchSection(handle, signal) {
  if (cache.has(handle)) return cache.get(handle);
  const res = await fetch(`/products/${handle}?section_id=quick-view`, { signal });
  if (!res.ok) throw new Error(`quick-view ${res.status}`);
  const html = await res.text();
  cache.set(handle, html);
  return html;
}

function focusFirst(scope) {
  scope.querySelector('.qv__btn, button, [href]')?.focus({ preventScroll: true });
}

async function openQuickView(trigger) {
  const handle = trigger.dataset.handle;
  const modal = getModal();

  if (!handle) return;
  if (!modal) {
    window.location.href = `/products/${handle}`;
    return;
  }

  const body = modal.querySelector('[data-qv-body]');
  lastTrigger = trigger;

  if (!modal.open) modal.showModal();

  // Same product already rendered - no request, no re-render.
  if (modal.dataset.handle === handle) {
    focusFirst(body);
    return;
  }

  // Cancel a fetch still in flight from another card.
  controller?.abort();
  controller = new AbortController();

  body.setAttribute('aria-busy', 'true');
  body.querySelector('.qv')?.remove();
  modal.dataset.handle = '';

  try {
    const html = await fetchSection(handle, controller.signal);
    body.insertAdjacentHTML('beforeend', html);
    modal.dataset.handle = handle;
    focusFirst(body);
  } catch (err) {
    if (err.name === 'AbortError') return;
    modal.close();
    window.location.href = `/products/${handle}`;
  } finally {
    body.removeAttribute('aria-busy');
  }
}

/* ---------------- delegated card triggers ---------------- */

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-qv-trigger]');
  if (!trigger) return;
  event.preventDefault();
  openQuickView(trigger);
});

document.addEventListener(
  'mouseover',
  (event) => {
    const trigger = event.target.closest('[data-qv-trigger]');
    if (!trigger) return;
    const { handle } = trigger.dataset;
    if (!handle || cache.has(handle)) return;

    clearTimeout(prefetchTimer);
    prefetchTimer = setTimeout(() => {
      fetchSection(handle).catch(() => {});
    }, PREFETCH_DELAY);
  },
  { passive: true }
);

document.addEventListener(
  'mouseout',
  (event) => {
    if (event.target.closest('[data-qv-trigger]')) clearTimeout(prefetchTimer);
  },
  { passive: true }
);

/* ---------------- gallery ---------------- */

class QuickViewGallery extends HTMLElement {
  connectedCallback() {
    this.frames = [...this.querySelectorAll('[data-qv-frame]')];
    this.thumbs = [...this.querySelectorAll('[data-qv-thumb]')];
    this.index = 0;

    this.querySelector('[data-qv-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-qv-next]')?.addEventListener('click', () => this.step(1));
    this.querySelector('[data-qv-zoom]')?.addEventListener('click', () => this.zoom());

    this.querySelectorAll('[data-qv-image]').forEach((button) => {
      button.addEventListener('click', () => this.showImage(button));
    });

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.qvThumb)));
    });

    /* Thumb strip scrolling. The arrows hide when there is nothing to scroll
       to, so they never sit there inert at either end of the strip. */
    this.strip = this.querySelector('[data-qv-thumbs]');
    this.thumbPrev = this.querySelector('[data-qv-thumbs-prev]');
    this.thumbNext = this.querySelector('[data-qv-thumbs-next]');

    this.thumbPrev?.addEventListener('click', () => this.scrollThumbs(-1));
    this.thumbNext?.addEventListener('click', () => this.scrollThumbs(1));
    this.strip?.addEventListener('scroll', () => this.syncThumbNav(), { passive: true });

    // Images are still loading on first paint, so scrollWidth is not final yet.
    this.syncThumbNav();
    requestAnimationFrame(() => this.syncThumbNav());
    window.addEventListener('resize', this.onResize = () => this.syncThumbNav(), { passive: true });

    /* Escape has three possible targets stacked on each other. Captured at
       document level so this runs before the dialog's own handler, and only
       falls through to closing the modal when nothing else is open. */
    this.onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (!this.querySelector('.qv__imgpop')) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeImage();
    };
    document.addEventListener('keydown', this.onKey, true);

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey, true);
    window.removeEventListener('resize', this.onResize);
  }

  scrollThumbs(direction) {
    if (!this.strip) return;
    // Roughly three thumbs at a time - far enough to feel like progress,
    // short enough that nothing scrolls past unseen.
    this.strip.scrollBy({ left: direction * 246, behavior: 'smooth' });
  }

  syncThumbNav() {
    if (!this.strip || !this.thumbPrev || !this.thumbNext) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.strip;
    const overflows = scrollWidth - clientWidth > 1;

    this.thumbPrev.hidden = !overflows || scrollLeft <= 1;
    this.thumbNext.hidden = !overflows || scrollLeft >= scrollWidth - clientWidth - 1;
  }

  step(delta) {
    if (!this.frames.length) return;
    this.go((this.index + delta + this.frames.length) % this.frames.length);
  }

  go(next) {
    this.frames[this.index]?.classList.remove('is-active');
    this.thumbs[this.index]?.classList.remove('is-active');
    this.thumbs[this.index]?.setAttribute('aria-selected', 'false');

    this.index = next;

    this.frames[next]?.classList.add('is-active');
    this.thumbs[next]?.classList.add('is-active');
    this.thumbs[next]?.setAttribute('aria-selected', 'true');
    this.thumbs[next]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    this.syncThumbNav();
  }

  zoom() {
    const src = this.frames[this.index]?.querySelector('img')?.src;
    if (!src) return;

    const overlay = document.createElement('div');
    overlay.className = 'qv__zoom';
    overlay.innerHTML = `<img src="${src}" alt="">`;

    const close = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey, true);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation(); // do not let Escape also close the dialog
        close();
      }
    };

    overlay.addEventListener('click', close);
    document.addEventListener('keydown', onKey, true);
    (getModal() || document.body).appendChild(overlay);
  }

  /**
   * Image popup for the dimension and spec openers. Lives inside the media
   * stage rather than being a second <dialog>: nested modal dialogs stack in
   * the top layer and both answer Escape, so the outer modal would close
   * underneath this one.
   *
   * Clicking the same opener again closes it, which is what a toggle button
   * with aria-expanded promises.
   */
  showImage(button) {
    const src = button.dataset.qvImage;
    const stage = this.querySelector('.qv__stage');
    if (!src || !stage) return;

    const open = stage.querySelector('.qv__imgpop');
    const sameSource = open?.dataset.src === src;

    this.closeImage();
    if (sameSource) return;

    const pop = document.createElement('div');
    pop.className = 'qv__imgpop';
    pop.dataset.src = src;
    pop.innerHTML = `
      <button type="button" class="qv__imgpop-close" aria-label="Close">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      </button>
      <img src="${src}" alt="${button.dataset.qvImageAlt || ''}">`;

    // Backdrop click closes; a click on the image itself does not.
    pop.addEventListener('click', (event) => {
      if (event.target === pop || event.target.closest('.qv__imgpop-close')) this.closeImage();
    });

    stage.appendChild(pop);
    button.setAttribute('aria-expanded', 'true');
    pop.querySelector('.qv__imgpop-close')?.focus({ preventScroll: true });
  }

  closeImage() {
    this.querySelector('.qv__imgpop')?.remove();
    this.querySelectorAll('[data-qv-image]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }
}

/* ---------------- sample add ---------------- */

/**
 * Horizon's cart drawer auto-opens on StandardEvents.cartLinesUpdate when the
 * event's `action` is 'add', and re-renders once `promise` settles. Both are
 * read as PROPERTIES on the event, not from detail.
 *
 * The event name is read from the theme's own module rather than hardcoded, so
 * it survives Shopify renaming it. The import is dynamic and wrapped: a static
 * import would take the whole file down if the specifier were ever absent from
 * the import map.
 *
 * Lifted from blocks/buy-panel.liquid. If that contract changes, change it here.
 */
async function notifyCart() {
  let eventName = 'cart:lines-update';

  try {
    const { StandardEvents } = await import('@shopify/events');
    eventName = StandardEvents?.cartLinesUpdate ?? eventName;
  } catch {
    // Import map entry missing; fall through to the literal name
  }

  const event = new CustomEvent(eventName, { bubbles: true });
  event.action = 'add';
  event.promise = Promise.resolve({ detail: { didError: false } });

  // Dispatched from document deliberately: the drawer calls
  // closest('dialog:modal') on the target, and document is not an Element, so
  // it skips the modal-deferral branch and opens immediately.
  document.dispatchEvent(event);
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-qv-sample]');
  if (!button || button.getAttribute('aria-disabled') === 'true') return;

  const label = button.querySelector('[data-qv-sample-label]') || button;
  const feedback = button.closest('.qv')?.querySelector('[data-qv-feedback]');
  const original = label.textContent;

  button.setAttribute('aria-disabled', 'true');
  label.textContent = 'Adding...';
  if (feedback) feedback.textContent = '';

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
    });

    const payload = await res.json();
    if (!res.ok) throw new Error(payload.description || res.status);

    label.textContent = 'Sample added';

    /* Close before notifying. The drawer is itself a modal <dialog>, and the
       top layer stacks by order of showModal() - leaving quick-view open would
       put the drawer behind it, or trap focus in the wrong one. */
    getModal()?.close();
    await notifyCart();

    setTimeout(() => {
      label.textContent = original;
      button.removeAttribute('aria-disabled');
    }, 2000);
  } catch (err) {
    label.textContent = original;
    button.removeAttribute('aria-disabled');
    if (feedback) feedback.textContent = `Could not add the sample. ${err.message}`;
  }
});

/* ---------------- dialog wiring ---------------- */

document.addEventListener('click', (event) => {
  const modal = getModal();
  if (!modal?.open) return;

  if (event.target.closest('[data-qv-close]')) {
    modal.close();
    return;
  }

  // Backdrop click lands on the <dialog> itself, never on a child.
  if (event.target === modal) modal.close();
});

document.addEventListener('DOMContentLoaded', () => {
  getModal()?.addEventListener('close', () => {
    lastTrigger?.focus({ preventScroll: true });
    lastTrigger = null;
  });
});

if (!customElements.get('quick-view-gallery')) {
  customElements.define('quick-view-gallery', QuickViewGallery);
}